import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Row, Col, Alert, Card, Collapse } from 'react-bootstrap';
import { HTTPService } from "../../services/httpService";

const API = new HTTPService();

export class ServiceOverridesModal extends Component {
    state = {
        // Available options
        categories: [],
        services: [], // All services for this installer
        modelTrims: [], // All trims for this model
        
        // Current overrides - keyed by service_id
        existingOverrides: {},
        
        // Model-level overrides - keyed by master_service_id
        modelOverrides: {},
        
        // New trim override inputs - keyed by master_service_id
        newTrimOverrides: {},
        
        // UI state
        selectedCategory: '', // Will be set to first category when loaded
        selectedTrim: 'ALL',
        expandedServices: {},
        
        // Loading states
        loading: false,
        saving: false,
        
        // Errors
        errors: {}
    };

    async componentDidMount() {
        if (this.props.show) {
            await this.loadInitialData();
        }
    }

    async componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show) {
            await this.loadInitialData();
        }
    }

    // ======================
    // DATA LOADING METHODS
    // ======================

    loadInitialData = async () => {
        this.setState({ loading: true });
        try {
            // Load categories first and get them back immediately
            const categories = await this.loadCategories();
            
            // Load services and model trims in parallel
            await Promise.all([
                this.loadServices(categories),
                this.loadModelTrims()
            ]);
            
            // Now load existing overrides after we have model trims
            await this.loadExistingOverrides();
            
        } catch (error) {
            console.error("Error loading initial data:", error);
            this.setState({ 
                errors: { general: "Failed to load data. Please try again." }
            });
        } finally {
            this.setState({ loading: false });
        }
    }

    loadCategories = async () => {
        try {
            const { data: categories } = await API.get(`/categories/${this.props.installerId}`);
            
            let categoryList = [];
            if (Array.isArray(categories)) {
                categoryList = categories.map(category => {
                    if (typeof category === 'object' && category !== null) {
                        return category.service_category || 
                               category.category_name || 
                               category.name || 
                               category.category || 
                               String(category);
                    }
                    return String(category);
                });
            }
            
            this.setState({ 
                categories: categoryList,
                // Set first category as default
                selectedCategory: categoryList.length > 0 ? categoryList[0] : ''
            });
            
            // Return categories so we can use them immediately
            return categoryList;
        } catch (error) {
            console.error("Error loading categories:", error);
            this.setState({ categories: [], selectedCategory: '' });
            return [];
        }
    }

    loadServices = async (categories = null) => {
        try {
            const categoriesToUse = categories || this.state.categories;
            
            if (!categoriesToUse || categoriesToUse.length === 0) {
                this.setState({ services: [] });
                return;
            }
            
            // Get all services for this installer
            const allServicesPromises = categoriesToUse.map(category =>
                API.get(`/categoryServices/${this.props.installerId}/${category}`)
                    .then(response => response)
                    .catch(error => {
                        console.error(`Error loading category ${category}:`, error);
                        return { data: [] };
                    })
            );
            
            const allServiceResponses = await Promise.all(allServicesPromises);
            
            // Flatten and process all services
            const allServices = allServiceResponses.flatMap(response => response.data || []);
            
            // Group by master_service_id to create our service structure
            const servicesMap = new Map();
            
            allServices.forEach(service => {
                if (!servicesMap.has(service.master_service_id)) {
                    servicesMap.set(service.master_service_id, {
                        master_service_id: service.master_service_id,
                        description: service.description,
                        category: service.service_category,
                        // Store services by size and catalog
                        retailServices: {}, // { S: {service_id, price}, M: {service_id, price}, L: {service_id, price} }
                        wholesaleServices: {} // { S: {service_id, price}, M: {service_id, price}, L: {service_id, price} }
                    });
                }
                
                const masterService = servicesMap.get(service.master_service_id);
                const vehicleSize = service.vehicle_size; // S, M, or L
                
                if (service.catalog === 'RETAIL') {
                    masterService.retailServices[vehicleSize] = {
                        service_id: service.service_id,
                        price: service.service_cost || 0
                    };
                } else if (service.catalog === 'WHOLE') {
                    masterService.wholesaleServices[vehicleSize] = {
                        service_id: service.service_id,
                        price: service.service_cost || 0
                    };
                }
            });

            const services = Array.from(servicesMap.values());
            this.setState({ services });
        } catch (error) {
            console.error("Error loading services:", error);
            this.setState({ services: [] });
        }
    }

    loadModelTrims = async () => {
        try {
            const { data: allVehicles } = await API.get(`/vehiclescatalog/${this.props.installerId}`);
            
            // Filter to same make/model but get all trims
            const modelTrims = allVehicles.filter(vehicle => 
                vehicle.make_name === this.props.vehicle.make_name &&
                vehicle.model_name === this.props.vehicle.model_name
            );
            
            this.setState({ modelTrims });
            return modelTrims;
        } catch (error) {
            console.error("Error loading model trims:", error);
            this.setState({ modelTrims: [] });
            return [];
        }
    }

    loadExistingOverrides = async () => {
        try {
            if (this.state.modelTrims.length === 0) {
                this.setState({ existingOverrides: {} });
                return;
            }

            console.log(`Loading overrides for ${this.state.modelTrims.length} trims:`, this.state.modelTrims);

            // Load overrides for all trims of this model
            const overridePromises = this.state.modelTrims.map(trim =>
                API.get(`/service-overrides-simple/${this.props.installerId}/${trim.id_car_trim}`)
                    .then(response => {
                        console.log(`Successfully loaded overrides for trim ${trim.trim_name} (ID: ${trim.id_car_trim}):`, response.data);
                        return { trimId: trim.id_car_trim, trimName: trim.trim_name, overrides: response.data || [] };
                    })
                    .catch(error => {
                        console.error(`Error loading overrides for ${trim.trim_name} (ID: ${trim.id_car_trim}):`, error.response?.status, error.message);
                        return { trimId: trim.id_car_trim, trimName: trim.trim_name, overrides: [] };
                    })
            );

            const allOverrides = await Promise.all(overridePromises);

            // Organize overrides by service_id
            const existingOverrides = {};

            allOverrides.forEach(({ trimId, trimName, overrides }) => {
                overrides.forEach(override => {
                    const serviceId = override.service_id;

                    if (!existingOverrides[serviceId]) {
                        existingOverrides[serviceId] = [];
                    }

                    existingOverrides[serviceId].push({
                        trimId,
                        trimName,
                        catalog: override.catalog,
                        price: override.override_price,
                        overrideId: override.id
                    });
                });
            });

            console.log('Processed existing overrides by service_id:', existingOverrides);
            this.setState({ existingOverrides });
        } catch (error) {
            console.error("Error loading existing overrides:", error);
            this.setState({ existingOverrides: {} });
        }
    }

    // ======================
    // HELPER METHODS
    // ======================

    // Helper method to get the model's vehicle size
    getModelSize = () => {
        // Get the vehicle size from the first trim (all trims of a model should have the same size)
        return this.state.modelTrims.length > 0 ? this.state.modelTrims[0].id_car_type : 'M';
    }

    // Helper method to get the appropriate service for this model's size
    getServiceForModel = (service, catalog) => {
        const modelSize = this.getModelSize();
        if (catalog === 'RETAIL') {
            return service.retailServices[modelSize];
        } else if (catalog === 'WHOLESALE') {
            return service.wholesaleServices[modelSize];
        }
        return null;
    }

    getTrimOverridesForService = (serviceId) => {
        const service = this.state.services.find(s => s.master_service_id === serviceId);
        if (!service) {
            return [];
        }
        
        const modelSize = this.getModelSize();
        const retailService = service.retailServices[modelSize];
        const wholesaleService = service.wholesaleServices[modelSize];
        
        // Get overrides for both retail and wholesale service IDs
        const retailOverrides = retailService ? (this.state.existingOverrides[retailService.service_id] || []) : [];
        const wholesaleOverrides = wholesaleService ? (this.state.existingOverrides[wholesaleService.service_id] || []) : [];
        
        // Group by trim
        const trimOverrides = {};
        
        // Process retail overrides
        retailOverrides.forEach(override => {
            if (!trimOverrides[override.trimId]) {
                trimOverrides[override.trimId] = {
                    trimName: override.trimName,
                    retail: null,
                    wholesale: null
                };
            }
            trimOverrides[override.trimId].retail = {
                price: override.price,
                overrideId: override.overrideId
            };
        });
        
        // Process wholesale overrides
        wholesaleOverrides.forEach(override => {
            if (!trimOverrides[override.trimId]) {
                trimOverrides[override.trimId] = {
                    trimName: override.trimName,
                    retail: null,
                    wholesale: null
                };
            }
            trimOverrides[override.trimId].wholesale = {
                price: override.price,
                overrideId: override.overrideId
            };
        });
        
        return Object.entries(trimOverrides).map(([trimId, data]) => ({
            trimId: parseInt(trimId),
            ...data
        }));
    }

    getFilteredServices = () => {
        let filtered = this.state.services;

        // Filter by category (no "ALL" option anymore)
        if (this.state.selectedCategory) {
            filtered = filtered.filter(service => 
                service.category === this.state.selectedCategory
            );
        }

        // Filter by trim (show services that either have no overrides or have overrides for selected trim)
        if (this.state.selectedTrim !== 'ALL') {
            filtered = filtered.filter(service => {
                const trimOverrides = this.getTrimOverridesForService(service.master_service_id);
                return trimOverrides.length === 0 || 
                       trimOverrides.some(override => override.trimName === this.state.selectedTrim);
            });
        }

        return filtered;
    }

    hasModelOverride = (serviceId) => {
        const overrides = this.state.modelOverrides[serviceId] || {};
        return overrides.retail || overrides.wholesale;
    }

    getTrimCount = (serviceId) => {
        const trimOverrides = this.getTrimOverridesForService(serviceId);
        return trimOverrides.length;
    }

    hasConflict = (serviceId) => {
        return this.hasModelOverride(serviceId) && this.getTrimCount(serviceId) > 0;
    }

    // ======================
    // EVENT HANDLERS
    // ======================

    handleCategoryChange = (category) => {
        this.setState({ selectedCategory: category });
    }

    handleTrimChange = (trim) => {
        this.setState({ selectedTrim: trim });
    }

    toggleService = (serviceId) => {
        this.setState(prev => ({
            expandedServices: {
                ...prev.expandedServices,
                [serviceId]: !prev.expandedServices[serviceId]
            }
        }));
    }

    handleModelOverride = (serviceId, catalog, price) => {
        this.setState(prev => ({
            modelOverrides: {
                ...prev.modelOverrides,
                [serviceId]: {
                    ...prev.modelOverrides[serviceId],
                    [catalog]: price
                }
            }
        }));
    }

    handleNewTrimOverride = (serviceId, field, value) => {
        this.setState(prev => ({
            newTrimOverrides: {
                ...prev.newTrimOverrides,
                [serviceId]: {
                    ...prev.newTrimOverrides[serviceId],
                    [field]: value
                }
            }
        }));
    }

    // ======================
    // SAVE/DELETE METHODS
    // ======================

    saveModelOverride = async (serviceId) => {
        const service = this.state.services.find(s => s.master_service_id === serviceId);
        const overrides = this.state.modelOverrides[serviceId] || {};
        
        if (!service || (!overrides.retail && !overrides.wholesale)) return;

        this.setState({ saving: true });
        
        try {
            const promises = [];
            
            // Get all trims for this model
            const targetTrims = this.state.modelTrims;
            
            // Handle retail overrides
            if (overrides.retail) {
                targetTrims.forEach(trim => {
                    const vehicleSize = trim.id_car_type; // S, M, or L
                    const retailService = service.retailServices[vehicleSize];
                    
                    if (retailService) {
                        const retailData = {
                            serviceId: retailService.service_id,
                            installerId: this.props.installerId,
                            catalog: 'RETAIL',
                            idCarTrim: trim.id_car_trim,
                            overridePrice: parseFloat(overrides.retail)
                        };
                        promises.push(API.post('/service-trim-overrides', retailData));
                    }
                });
            }
            
            // Handle wholesale overrides
            if (overrides.wholesale) {
                targetTrims.forEach(trim => {
                    const vehicleSize = trim.id_car_type; // S, M, or L
                    const wholesaleService = service.wholesaleServices[vehicleSize];
                    
                    if (wholesaleService) {
                        const wholesaleData = {
                            serviceId: wholesaleService.service_id,
                            installerId: this.props.installerId,
                            catalog: 'WHOLE',
                            idCarTrim: trim.id_car_trim,
                            overridePrice: parseFloat(overrides.wholesale)
                        };
                        promises.push(API.post('/service-trim-overrides', wholesaleData));
                    }
                });
            }
            
            await Promise.all(promises);
            
            // Clear the model override inputs
            this.setState(prev => ({
                modelOverrides: {
                    ...prev.modelOverrides,
                    [serviceId]: {}
                }
            }));
            
            // Reload existing overrides
            await this.loadExistingOverrides();
            
            // Notify parent
            if (this.props.onOverridesUpdated) {
                this.props.onOverridesUpdated();
            }
            
        } catch (error) {
            console.error("Error saving model override:", error);
            this.setState({
                errors: { general: "Failed to save model override. Please try again." }
            });
        } finally {
            this.setState({ saving: false });
        }
    }

    saveTrimOverride = async (serviceId) => {
        const service = this.state.services.find(s => s.master_service_id === serviceId);
        const newOverride = this.state.newTrimOverrides[serviceId] || {};
        
        if (!service || !newOverride.trimName || (!newOverride.retailPrice && !newOverride.wholesalePrice)) {
            return;
        }

        // Find ALL trims with the selected name (there might be multiple for different years)
        const selectedTrims = this.state.modelTrims.filter(t => t.trim_name === newOverride.trimName);
        if (selectedTrims.length === 0) {
            console.error('Selected trim not found');
            return;
        }

        this.setState({ saving: true });
        
        try {
            const promises = [];
            
            // Create overrides for ALL trim records with this name
            selectedTrims.forEach(selectedTrim => {
                const vehicleSize = selectedTrim.id_car_type;
                
                // Handle retail override
                if (newOverride.retailPrice) {
                    const retailService = service.retailServices[vehicleSize];
                    if (retailService) {
                        promises.push(
                            API.post('/service-trim-overrides', {
                                serviceId: retailService.service_id,
                                installerId: this.props.installerId,
                                catalog: 'RETAIL',
                                idCarTrim: selectedTrim.id_car_trim,
                                overridePrice: parseFloat(newOverride.retailPrice)
                            })
                        );
                    }
                }
                
                // Handle wholesale override
                if (newOverride.wholesalePrice) {
                    const wholesaleService = service.wholesaleServices[vehicleSize];
                    if (wholesaleService) {
                        promises.push(
                            API.post('/service-trim-overrides', {
                                serviceId: wholesaleService.service_id,
                                installerId: this.props.installerId,
                                catalog: 'WHOLE',
                                idCarTrim: selectedTrim.id_car_trim,
                                overridePrice: parseFloat(newOverride.wholesalePrice)
                            })
                        );
                    }
                }
            });
            
            await Promise.all(promises);
            
            // Clear the new trim override inputs
            this.setState(prev => ({
                newTrimOverrides: {
                    ...prev.newTrimOverrides,
                    [serviceId]: {}
                }
            }));
            
            // Reload existing overrides
            await this.loadExistingOverrides();
            
            // Notify parent
            if (this.props.onOverridesUpdated) {
                this.props.onOverridesUpdated();
            }
            
        } catch (error) {
            console.error("Error saving trim override:", error);
            this.setState({
                errors: { general: "Failed to save trim override. Please try again." }
            });
        } finally {
            this.setState({ saving: false });
        }
    }

    deleteOverride = async (overrideId) => {
        if (!window.confirm('Are you sure you want to delete this service override?')) {
            return;
        }
        
        try {
            await API.delete(`/service-trim-overrides/${overrideId}`);
            
            // Reload existing overrides
            await this.loadExistingOverrides();
            
            // Notify parent
            if (this.props.onOverridesUpdated) {
                this.props.onOverridesUpdated();
            }
            
        } catch (error) {
            console.error("Error deleting override:", error);
        }
    }

    // ======================
    // RENDER METHODS
    // ======================

    renderServiceRow = (service) => {
        const isExpanded = this.state.expandedServices[service.master_service_id];
        const hasModelOverride = this.hasModelOverride(service.master_service_id);
        const trimCount = this.getTrimCount(service.master_service_id);
        const hasConflict = this.hasConflict(service.master_service_id);
        const trimOverrides = this.getTrimOverridesForService(service.master_service_id);

        return (
            <Card key={service.master_service_id} className="mb-2">
                <Card.Header 
                    className="cursor-pointer py-2"
                    onClick={() => this.toggleService(service.master_service_id)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} mr-2 text-muted`}></i>
                            <div>
                                <h6 className="mb-0">{service.description}</h6>
                                <small className="text-muted">
                                    {(() => {
                                        const modelSize = this.getModelSize();
                                        const retailService = this.getServiceForModel(service, 'RETAIL');
                                        const wholesaleService = this.getServiceForModel(service, 'WHOLESALE');
                                        return `Size ${modelSize} - Retail: $${retailService?.price?.toLocaleString() || 'N/A'} | Wholesale: $${wholesaleService?.price?.toLocaleString() || 'N/A'}`;
                                    })()}
                                </small>
                            </div>
                        </div>
                        
                        <div className="d-flex align-items-center">
                            {hasModelOverride && (
                                <span className="badge badge-primary mr-2">Model Override</span>
                            )}
                            {trimCount > 0 && (
                                <span className="badge badge-success mr-2">
                                    {trimCount} Trim{trimCount !== 1 ? 's' : ''}
                                </span>
                            )}
                            {hasConflict && (
                                <i className="fa fa-exclamation-triangle text-warning mr-2"></i>
                            )}
                        </div>
                    </div>
                </Card.Header>

                <Collapse in={isExpanded}>
                    <Card.Body className="bg-light">
                        {/* Model Override Section */}
                        <div className="mb-4">
                            <h6 className="text-secondary">Model Override - Set prices for all trims</h6>
                            <Row>
                                <Col md={5}>
                                    <Form.Group>
                                        <Form.Label className="small">Retail Price</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            placeholder={(() => {
                                                const retailService = this.getServiceForModel(service, 'RETAIL');
                                                return `Current: $${retailService?.price?.toLocaleString() || 'N/A'}`;
                                            })()}
                                            value={this.state.modelOverrides[service.master_service_id]?.retail || ''}
                                            onChange={(e) => this.handleModelOverride(service.master_service_id, 'retail', e.target.value)}
                                            disabled={!this.getServiceForModel(service, 'RETAIL')}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Group>
                                        <Form.Label className="small">Wholesale Price</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            placeholder={(() => {
                                                const wholesaleService = this.getServiceForModel(service, 'WHOLESALE');
                                                return `Current: $${wholesaleService?.price?.toLocaleString() || 'N/A'}`;
                                            })()}
                                            value={this.state.modelOverrides[service.master_service_id]?.wholesale || ''}
                                            onChange={(e) => this.handleModelOverride(service.master_service_id, 'wholesale', e.target.value)}
                                            disabled={!this.getServiceForModel(service, 'WHOLESALE')}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => this.saveModelOverride(service.master_service_id)}
                                        disabled={!this.hasModelOverride(service.master_service_id) || this.state.saving}
                                        className="w-100"
                                    >
                                        {this.state.saving ? (
                                            <i className="fa fa-spinner fa-spin"></i>
                                        ) : (
                                            'Set All'
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                            
                            {hasConflict && (
                                <Alert variant="warning" className="py-2 mt-2">
                                    <small>
                                        <i className="fa fa-exclamation-triangle mr-1"></i>
                                        Will overwrite {trimCount} existing trim override{trimCount !== 1 ? 's' : ''}
                                    </small>
                                </Alert>
                            )}
                        </div>

                        {/* Trim Overrides Section */}
                        <div>
                            <h6 className="text-secondary">Trim-Specific Overrides</h6>
                            
                            {trimOverrides.length === 0 ? (
                                <p className="text-muted small">No trim-specific overrides set.</p>
                            ) : (
                                trimOverrides.map(override => (
                                    <Row key={override.trimId} className="mb-2 align-items-center">
                                        <Col md={3}>
                                            <strong className="small">{override.trimName}</strong>
                                        </Col>
                                        <Col md={3}>
                                            <div className="small">
                                                Retail: {override.retail ? `$${parseFloat(override.retail.price).toFixed(2)}` : 'Not set'}
                                                {override.retail && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0 ml-2 text-danger"
                                                        onClick={() => this.deleteOverride(override.retail.overrideId)}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </Button>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="small">
                                                Wholesale: {override.wholesale ? `$${parseFloat(override.wholesale.price).toFixed(2)}` : 'Not set'}
                                                {override.wholesale && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0 ml-2 text-danger"
                                                        onClick={() => this.deleteOverride(override.wholesale.overrideId)}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </Button>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <small className="text-muted">Individual trim pricing</small>
                                        </Col>
                                    </Row>
                                ))
                            )}
                            
                            {/* Add New Trim Override */}
                            <div className="mt-3 pt-3 border-top">
                                <h6 className="text-secondary small">Add Trim Override</h6>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Control
                                                as="select"
                                                size="sm"
                                                value={this.state.newTrimOverrides[service.master_service_id]?.trimName || ''}
                                                onChange={(e) => this.handleNewTrimOverride(service.master_service_id, 'trimName', e.target.value)}
                                            >
                                                <option value="">Select trim...</option>
                                                {[...new Set(this.state.modelTrims.map(t => t.trim_name))]
                                                    .sort()
                                                    .map(trimName => (
                                                        <option key={trimName} value={trimName}>
                                                            {trimName}
                                                        </option>
                                                    ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                size="sm"
                                                placeholder="Retail price"
                                                value={this.state.newTrimOverrides[service.master_service_id]?.retailPrice || ''}
                                                onChange={(e) => this.handleNewTrimOverride(service.master_service_id, 'retailPrice', e.target.value)}
                                                disabled={!this.getServiceForModel(service, 'RETAIL')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                size="sm"
                                                placeholder="Wholesale price"
                                                value={this.state.newTrimOverrides[service.master_service_id]?.wholesalePrice || ''}
                                                onChange={(e) => this.handleNewTrimOverride(service.master_service_id, 'wholesalePrice', e.target.value)}
                                                disabled={!this.getServiceForModel(service, 'WHOLESALE')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => this.saveTrimOverride(service.master_service_id)}
                                            disabled={
                                                !this.state.newTrimOverrides[service.master_service_id]?.trimName ||
                                                (!this.state.newTrimOverrides[service.master_service_id]?.retailPrice && 
                                                 !this.state.newTrimOverrides[service.master_service_id]?.wholesalePrice) ||
                                                this.state.saving
                                            }
                                            className="w-100"
                                        >
                                            {this.state.saving ? (
                                                <i className="fa fa-spinner fa-spin"></i>
                                            ) : (
                                                <>
                                                    <i className="fa fa-plus mr-1"></i>
                                                    Add Override
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Card.Body>
                </Collapse>
            </Card>
        );
    }

    render() {
        const { show, onHide, vehicle } = this.props;
        const { 
            loading,
            categories,
            modelTrims,
            selectedCategory,
            selectedTrim,
            errors 
        } = this.state;

        const filteredServices = this.getFilteredServices();
        const categoryOptions = categories; // No more "ALL" option
        const uniqueTrimNames = [...new Set(modelTrims.map(t => t.trim_name))].sort();
        const trimOptions = ['ALL', ...uniqueTrimNames];

        return (
            <Modal show={show} onHide={onHide} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Service Overrides - {vehicle?.make_name} {vehicle?.model_name}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading service data...</p>
                        </div>
                    ) : (
                        <>
                            {errors.general && (
                                <Alert variant="danger">{errors.general}</Alert>
                            )}

                            {/* Filters */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small font-weight-bold">Service Category:</Form.Label>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={selectedCategory}
                                            onChange={(e) => this.handleCategoryChange(e.target.value)}
                                        >
                                            {categoryOptions.map(category => (
                                                <option key={category} value={category}>
                                                    {category}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small font-weight-bold">Trim Focus:</Form.Label>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={selectedTrim}
                                            onChange={(e) => this.handleTrimChange(e.target.value)}
                                        >
                                            {trimOptions.map(trim => (
                                                <option key={trim} value={trim}>
                                                    {trim === 'ALL' ? 'All Trims' : trim}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="d-flex align-items-end">
                                    <small className="text-muted">
                                        {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''}
                                    </small>
                                </Col>
                            </Row>

                            {/* Services List */}
                            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                {filteredServices.length === 0 ? (
                                    <Alert variant="info">
                                        No services found matching your filters.
                                    </Alert>
                                ) : (
                                    filteredServices.map(service => this.renderServiceRow(service))
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default ServiceOverridesModal;